import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve session ID for anonymous tracking
const getSessionId = () => {
  let sessionId = localStorage.getItem('rarifex_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('rarifex_session_id', sessionId);
  }
  return sessionId;
};

export const useTracking = () => {
  const { user } = useAuth();
  const sessionId = useRef(getSessionId());
  const userId = user?.id || null;

  // Track a generic event
  const trackEvent = useCallback(async (eventType, data = {}) => {
    try {
      // Rispetta l'opt-out privacy: se disattivato, non inviare nulla
      if (localStorage.getItem('rarifex_tracking_optout') === 'true') return;

      const payload = {
        event_type: eventType,
        user_id: userId,
        session_id: sessionId.current,
        ...data
      };

      // Fire and forget - don't await in production
      fetch(`${API_URL}/api/tracking/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(() => {}); // Silent fail
    } catch (e) {
      // Silent fail - tracking should never break the app
    }
  }, [userId]);

  // Track product view with duration
  const trackProductView = useCallback((productId, crafterId, categorySlug) => {
    const startTime = Date.now();
    
    // Track initial view
    trackEvent('view', {
      product_id: productId,
      crafter_id: crafterId,
      category_slug: categorySlug
    });

    // Return cleanup function to track duration
    return () => {
      const duration = Math.round((Date.now() - startTime) / 1000);
      if (duration > 2) { // Only track if viewed for more than 2 seconds
        trackEvent('view_duration', {
          product_id: productId,
          duration: duration
        });
      }
    };
  }, [trackEvent]);

  // Track category view
  const trackCategoryView = useCallback((categorySlug) => {
    trackEvent('category_view', { category_slug: categorySlug });
  }, [trackEvent]);

  // Track search
  const trackSearch = useCallback((query) => {
    if (query && query.trim().length > 1) {
      trackEvent('search', { search_query: query.trim() });
    }
  }, [trackEvent]);

  // Track favorite
  const trackFavorite = useCallback((productId, crafterId) => {
    trackEvent('favorite', {
      product_id: productId,
      crafter_id: crafterId
    });
  }, [trackEvent]);

  // Track purchase
  const trackPurchase = useCallback((productIds, crafterIds, totalAmount) => {
    productIds.forEach((productId, index) => {
      trackEvent('purchase', {
        product_id: productId,
        crafter_id: crafterIds[index] || null,
        metadata: { total_amount: totalAmount }
      });
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackProductView,
    trackCategoryView,
    trackSearch,
    trackFavorite,
    trackPurchase,
    sessionId: sessionId.current,
    userId: user?.id || null
  };
};

export default useTracking;
